import type { EnabledMfrrProductionUnit } from 'api/mfrr/mfrr.types'
import { Fragment, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { colors } from 'styles/variables'
import {
  CMBID_DIRECTIONS,
  formatCmReadOnlyValue,
  getMfrrDirectionBackground,
  getMfrrDirectionInnerBorder,
  getMfrrQuarterIndex,
  getMfrrRowLabel,
  MFRR_TABLE_CELL_BORDER,
  MFRR_TABLE_GROUP_BORDER,
} from '../MfrrBidTable/MfrrBidTable.helper'
import type { MfrrEamMarketResultViolation } from '../MfrrBidTable/MfrrBidTable.types'
import { getMfrrViolationRows, getMfrrViolationUnits } from '../MfrrBidTable/MfrrMarketResults.helper'

const COLUMNS_PER_UNIT = 2

type MfrrLockWarningTableProps = {
  violations: MfrrEamMarketResultViolation[]
  units: EnabledMfrrProductionUnit[]
  dateStartTime: ISODateTime
}

function getBorderRight(columnIndex: number, columnCount: number, innerBorder: string): string {
  if (columnIndex === columnCount - 1) {
    return 'none'
  }

  const isGroupEnd = columnIndex === 0 || (columnIndex - 1) % COLUMNS_PER_UNIT === 1

  return isGroupEnd ? MFRR_TABLE_GROUP_BORDER : innerBorder
}

export function MfrrLockWarningTable({ violations, units, dateStartTime }: MfrrLockWarningTableProps): ReactNode {
  const { t } = useTranslation()

  const violationUnits = getMfrrViolationUnits(violations, units)
  const rows = getMfrrViolationRows(violations)
  const columnCount = 1 + CMBID_DIRECTIONS.length * violationUnits.length * COLUMNS_PER_UNIT

  function getBidColumnIndex(directionIndex: number, unitIndex: number): number {
    return 1 + (directionIndex * violationUnits.length + unitIndex) * COLUMNS_PER_UNIT
  }

  return (
    <div className="mt-4 max-h-[32rem] overflow-auto rounded border border-gray-300">
      <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
        <colgroup>
          {Array.from({ length: columnCount }, (_unused, index) => (
            <col key={`lock-warning-col-${index}`} className="w-[7rem]" />
          ))}
        </colgroup>
        <thead className="sticky top-0 z-10 bg-gray-100">
          <tr>
            <th
              rowSpan={3}
              className="bg-gray-100 px-2 py-2 text-left text-xs font-semibold"
              style={{
                color: colors.black,
                borderRight: MFRR_TABLE_GROUP_BORDER,
                borderBottom: MFRR_TABLE_CELL_BORDER,
              }}
            >
              {t('Hour')}
            </th>
            {CMBID_DIRECTIONS.map((direction, directionIndex) => (
              <th
                key={direction}
                colSpan={violationUnits.length * COLUMNS_PER_UNIT}
                className="px-2 py-2 text-center text-xs font-semibold"
                style={{
                  backgroundColor: getMfrrDirectionBackground(direction),
                  color: colors.black,
                  borderRight: getBorderRight(
                    (directionIndex + 1) * violationUnits.length * COLUMNS_PER_UNIT,
                    columnCount,
                    'none'
                  ),
                  borderBottom: getMfrrDirectionInnerBorder(direction),
                }}
              >
                {direction === 'up' ? t('mFRR-EAM Up') : t('mFRR-EAM Down')}
              </th>
            ))}
          </tr>
          <tr>
            {CMBID_DIRECTIONS.flatMap((direction, directionIndex) =>
              violationUnits.map((unit, unitIndex) => (
                <th
                  key={`${direction}-${unit.id}`}
                  colSpan={COLUMNS_PER_UNIT}
                  className="px-2 py-2 text-center text-xs font-semibold"
                  style={{
                    backgroundColor: getMfrrDirectionBackground(direction),
                    color: colors.black,
                    borderRight: getBorderRight(getBidColumnIndex(directionIndex, unitIndex) + 1, columnCount, 'none'),
                    borderBottom: getMfrrDirectionInnerBorder(direction),
                  }}
                >
                  {unit.name}
                </th>
              ))
            )}
          </tr>
          <tr>
            {CMBID_DIRECTIONS.flatMap((direction, directionIndex) =>
              violationUnits.flatMap((unit, unitIndex) => {
                const bidColumnIndex = getBidColumnIndex(directionIndex, unitIndex)
                const innerBorder = getMfrrDirectionInnerBorder(direction)
                const headerStyle = {
                  backgroundColor: getMfrrDirectionBackground(direction),
                  color: colors.black,
                  borderBottom: MFRR_TABLE_CELL_BORDER,
                }

                return [
                  <th
                    key={`${direction}-${unit.id}-bid`}
                    className="px-2 py-2 text-center text-xs font-semibold"
                    style={{ ...headerStyle, borderRight: getBorderRight(bidColumnIndex, columnCount, innerBorder) }}
                  >
                    {t('Bid volume')}
                  </th>,
                  <th
                    key={`${direction}-${unit.id}-result`}
                    className="px-2 py-2 text-center text-xs font-semibold"
                    style={{
                      ...headerStyle,
                      borderRight: getBorderRight(bidColumnIndex + 1, columnCount, innerBorder),
                    }}
                  >
                    {t('CM Volume Result')}
                  </th>,
                ]
              })
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const previousRow = rows[index - 1]
            const quarterIndex = getMfrrQuarterIndex(row.startTime, dateStartTime)
            const hasTimeGap =
              previousRow !== undefined && quarterIndex - getMfrrQuarterIndex(previousRow.startTime, dateStartTime) > 1

            return (
              <Fragment key={row.startTime}>
                {hasTimeGap && (
                  <tr className="bg-gray-100">
                    {Array.from({ length: columnCount }, (_unused, cellIndex) => (
                      <td
                        key={`separator-${row.startTime}-${cellIndex}`}
                        className="px-2 py-1 text-center text-gray-500"
                        style={{
                          borderRight: getBorderRight(cellIndex, columnCount, MFRR_TABLE_CELL_BORDER),
                          borderBottom: MFRR_TABLE_CELL_BORDER,
                        }}
                      >
                        ...
                      </td>
                    ))}
                  </tr>
                )}
                <tr className="bg-white">
                  <td
                    className="px-2 py-2 text-left font-medium whitespace-nowrap"
                    style={{ borderRight: MFRR_TABLE_GROUP_BORDER, borderBottom: MFRR_TABLE_CELL_BORDER }}
                  >
                    {getMfrrRowLabel(dateStartTime, quarterIndex, 'eam')}
                  </td>
                  {CMBID_DIRECTIONS.flatMap((direction, directionIndex) =>
                    violationUnits.flatMap((unit, unitIndex) => {
                      const bidColumnIndex = getBidColumnIndex(directionIndex, unitIndex)
                      const violation = row.violationsByUnitId.get(unit.id)?.[direction]

                      return [
                        <td
                          key={`${direction}-${unit.id}-bid`}
                          className="px-2 py-2 text-right"
                          style={{
                            borderRight: getBorderRight(bidColumnIndex, columnCount, MFRR_TABLE_CELL_BORDER),
                            borderBottom: MFRR_TABLE_CELL_BORDER,
                          }}
                        >
                          {violation ? formatCmReadOnlyValue(violation.volume) : '-'}
                        </td>,
                        <td
                          key={`${direction}-${unit.id}-result`}
                          className="px-2 py-2 text-right"
                          style={{
                            borderRight: getBorderRight(bidColumnIndex + 1, columnCount, MFRR_TABLE_CELL_BORDER),
                            borderBottom: MFRR_TABLE_CELL_BORDER,
                          }}
                        >
                          {violation ? formatCmReadOnlyValue(violation.acceptedVolume) : '-'}
                        </td>,
                      ]
                    })
                  )}
                </tr>
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
